import type { SitePrices } from '../../api/client';
import { FLOWPOWER_DAILY_CAP_KWH } from '../../utils/flowpower';

interface HappyHourCardProps {
  prices: SitePrices | null;
  /** Site-local window, e.g. { startHour: 17.5, endHour: 21.5 }. */
  window: { startHour: number; endHour: number };
}

function formatHour(hour: number): string {
  const h = Math.floor(hour);
  const m = Math.round((hour - h) * 60);
  const suffix = h < 12 || h === 24 ? 'am' : 'pm';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12}${suffix}` : `${h12}:${String(m).padStart(2, '0')}${suffix}`;
}

/**
 * Flow Power Happy Hour status: today's export against the daily premium-rate
 * cap (measured by the price poller) and what the current export earns.
 */
export function HappyHourCard({ prices, window }: HappyHourCardProps) {
  const hh = prices?.happy_hour;
  const cap = hh?.daily_cap_kwh ?? FLOWPOWER_DAILY_CAP_KWH;
  const exported = Math.max(0, hh?.window_export_kwh ?? 0);
  const pct = Math.min(100, (exported / cap) * 100);
  const capReached = !!hh?.cap_reached_at;
  const exportCents = prices ? Math.round(prices.current.export * 1000) / 10 : null;
  const windowLabel = `${formatHour(window.startHour)}–${formatHour(window.endHour)}`;

  let status: string;
  if (!prices) {
    status = 'Waiting for the first price update…';
  } else if (exportCents === 0) {
    status = capReached
      ? `Today's ${cap} kWh at the Happy Hour rate is done.`
      : `Happy Hour runs ${windowLabel}. Outside it, exports earn nothing.`;
  } else if (capReached) {
    status = `Cap reached: exports now earn ${exportCents}c until the window closes.`;
  } else {
    status = `Happy Hour is on: exports earn ${exportCents}c/kWh.`;
  }

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-lg">🌇</span>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Flow Power Happy Hour</h3>
          <span className="text-xs px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
            {windowLabel}
          </span>
        </div>
        {prices && (
          <span className="text-xs text-gray-400">
            Updated {new Date(prices.timestamp).toLocaleTimeString('en-AU')}
          </span>
        )}
      </div>

      <div className="flex items-baseline justify-between mb-1">
        <span className="text-sm text-gray-600 dark:text-gray-400">Exported in today's window</span>
        <span className="text-sm font-semibold text-gray-900 dark:text-white">
          {exported.toFixed(1)} / {cap} kWh
        </span>
      </div>
      <div
        className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={cap}
        aria-valuenow={exported}
      >
        <div
          className={`h-full rounded-full ${capReached ? 'bg-green-500' : 'bg-brand-orange'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">{status}</p>
    </div>
  );
}
